import {
  DOCUMENT_EXTENSIONS,
  type DocumentDetail,
  type DocumentFormat,
  documentFormatOf,
  isPptxDocumentName,
  isSheetDocumentName,
  officeKindOf,
} from '@apm/contracts';

/**
 * Hộp soạn mở được tài liệu này bằng định dạng nào — `null` = không sửa trong
 * app được (APM-PDOC-c/-d).
 *
 * - Bản mới nhất là tệp ⇒ theo đuôi: `.chilldoc`, `.md`, `.txt` (ba loại hộp
 *   soạn hỗ trợ). `.docx`, `.pdf`… thì tải về để sửa.
 * - Bản mới nhất chỉ có **chữ gõ tay** (form "Tài liệu mới", khuôn dựng sẵn) ⇒
 *   markdown: chữ ấy vốn đã là markdown. Lưu từ hộp soạn là phiên bản `.md` mới.
 */
export function editableFormatOf(document: DocumentDetail): DocumentFormat | null {
  const latest = document.versions[0];
  if (latest?.filename) return documentFormatOf(latest.filename);
  return document.latestContent !== null ? 'markdown' : null;
}

/**
 * Tên trong hộp soạn: **tiêu đề** + đuôi — Lưu không đổi tiêu đề khi người dùng
 * không đổi tên (`projectDocumentStorage.save` so tên cũ với tên mới).
 */
export function editorNameOf(document: DocumentDetail, format: DocumentFormat): string {
  return `${document.title}${DOCUMENT_EXTENSIONS[format]}`;
}

/**
 * Hộp soạn **nào** mở được tài liệu này — APM-XLE-d, plan-sheet-editor §6:
 * hộp soạn chữ (theo `DocumentFormat`) hay hộp soạn bảng tính (`.xlsx`/`.xls`,
 * giữ tên tệp thật vì Lưu ghi `.xlsx`), hay hộp soạn PowerPoint (`.pptx`,
 * APM-PPX-i, plan-pptx-editor §8). `null` = tải về để sửa.
 */
export type EditableKind =
  | {
      readonly kind: 'document';
      readonly format: DocumentFormat;
      /** Tệp Office (APM-WDE-c): tên thật quyết định đường mở và đuôi ghi — không dựng từ tiêu đề. */
      readonly filename?: string;
    }
  | { readonly kind: 'sheet'; readonly filename: string }
  | { readonly kind: 'pptx'; readonly filename: string };

export function editableKindOf(document: DocumentDetail): EditableKind | null {
  const latest = document.versions[0];
  if (latest?.filename && isSheetDocumentName(latest.filename)) {
    return { kind: 'sheet', filename: latest.filename };
  }
  if (latest?.filename && isPptxDocumentName(latest.filename)) {
    return { kind: 'pptx', filename: latest.filename };
  }
  if (latest?.filename && officeKindOf(latest.filename)) {
    return { kind: 'document', format: 'chilldoc', filename: latest.filename };
  }
  const format = editableFormatOf(document);
  return format ? { kind: 'document', format } : null;
}
